const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Generic fetch wrapper with credentials
async function apiFetch(endpoint, options = {}) {
    const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Request failed' }));
        throw new Error(error.detail || 'Request failed');
    }

    return response.json();
}

// CV API
export const cvApi = {
    upload: async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API_URL}/cv/upload`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
            throw new Error(error.detail || 'Upload failed');
        }

        return response.json();
    },

    list: () => apiFetch('/cv/list'),

    get: (cvId) => apiFetch(`/cv/${cvId}`),

    delete: (cvId) => apiFetch(`/cv/${cvId}`, { method: 'DELETE' }),
};

// Jobs API
export const jobsApi = {
    search: (query = 'web3 developer jobs') => 
        apiFetch(`/jobs/search?q=${encodeURIComponent(query)}`),

    matchForCV: (cvId) => 
        apiFetch(`/jobs/match?cv_id=${cvId}`, { method: 'POST' }),
    
    trackClick: (job) => apiFetch('/jobs/track-click', {
        method: 'POST',
        body: JSON.stringify({
            job_id: job.job_id || job.url,
            title: job.title,
            company: job.company,
            url: job.url
        }),
    }),
};

// Favorites API
export const favoritesApi = {
    list: () => apiFetch('/favorites'),

    add: (job) => apiFetch('/favorites', {
        method: 'POST',
        body: JSON.stringify(job),
    }),

    remove: (favoriteId) => apiFetch(`/favorites/${favoriteId}`, {
        method: 'DELETE',
    }),
};

// Applied Jobs API
export const appliedApi = {
    list: () => apiFetch('/applied'),

    add: (job, notes = null) => apiFetch('/applied', {
        method: 'POST',
        body: JSON.stringify({ job, notes }),
    }),

    update: (appliedId, data) => apiFetch(`/applied/${appliedId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    }),

    remove: (appliedId) => apiFetch(`/applied/${appliedId}`, {
        method: 'DELETE',
    }),
};

// User API
export const userApi = {
    getProfile: () => apiFetch('/user/profile'),

    updateProfile: (data) => apiFetch('/user/profile', {
        method: 'PUT',
        body: JSON.stringify(data),
    }),

    deleteAllData: () => apiFetch('/user/delete-all-data', {
        method: 'DELETE',
    }),

    getStats: () => apiFetch('/stats'),
};

// Admin API
export const adminApi = {
    checkAdmin: () => apiFetch('/admin/check'),

    searchTalent: (params = {}) => apiFetch('/admin/talent/search', {
        method: 'POST',
        body: JSON.stringify({
            query: params.query || null,
            sector: params.sector || null,
            skills: params.skills || null,
            page: params.page || 1,
            limit: params.limit || 20
        }),
    }),

    getTalentDetails: (cvId) => apiFetch(`/admin/talent/${cvId}`),

    getStats: () => apiFetch('/admin/stats'),

    getSectors: () => apiFetch('/admin/sectors'),

    parseJD: async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API_URL}/admin/parse-jd`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });
        
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.detail || 'Failed to parse JD');
        }
        
        return response.json();
    },

    matchJD: (jobTitle, jdText) => apiFetch('/admin/match-jd', {
        method: 'POST',
        body: JSON.stringify({
            job_title: jobTitle,
            jd_text: jdText
        }),
    }),

    makeAdmin: (userId) => apiFetch(`/admin/make-admin/${userId}`, {
        method: 'POST',
    }),

    getUserClickedJobs: (userId) => apiFetch(`/admin/user/${userId}/clicked-jobs`),

    getCompanyContacts: (companyName) => apiFetch(`/admin/contacts?company_name=${encodeURIComponent(companyName)}`),

    requestMeeting: (candidateId, message, proposedTime) => apiFetch('/messages/request-meeting', {
        method: 'POST',
        body: JSON.stringify({
            candidate_id: candidateId,
            message: message,
            proposed_time: proposedTime
        }),
    }),
};

// Messages API
export const messagesApi = {
    getConversations: () => apiFetch('/messages/conversations'),

    getConversation: (conversationId) => apiFetch(`/messages/conversation/${conversationId}`),

    sendMessage: (data) => apiFetch('/messages/send', {
        method: 'POST',
        body: JSON.stringify(data),
    }),

    getUnreadCount: () => apiFetch('/messages/unread-count'),

    requestMeeting: (candidateId, message, proposedTime) => apiFetch('/messages/request-meeting', {
        method: 'POST',
        body: JSON.stringify({
            candidate_id: candidateId,
            message: message,
            proposed_time: proposedTime
        }),
    }),
};

// Integrations API (GitHub, Etherscan, Company Logos)
export const integrationsApi = {
    getGitHubStats: (username) => apiFetch(`/integrations/github/${username}`),
    
    getEtherscanStats: (address) => apiFetch(`/integrations/etherscan/${address}`),
    
    getCompanyLogo: (domain) => apiFetch(`/integrations/company-logo?domain=${encodeURIComponent(domain)}`),
    
    linkGitHub: (githubUsername) => apiFetch('/user/link-github', {
        method: 'POST',
        body: JSON.stringify({ github_username: githubUsername }),
    }),
    
    linkWallet: (walletAddress) => apiFetch('/user/link-wallet', {
        method: 'POST',
        body: JSON.stringify({ wallet_address: walletAddress }),
    }),
};

// Client Portal API
export const clientApi = {
    register: (email, name, companyName, password) => apiFetch('/client/register', {
        method: 'POST',
        body: JSON.stringify({ email, name, company_name: companyName, password }),
    }),
    
    login: (email, password) => apiFetch('/client/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
    }),
    
    logout: () => apiFetch('/client/logout', { method: 'POST' }),
    
    getProfile: () => apiFetch('/client/me'),
    
    uploadJD: async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API_URL}/client/jd/upload`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });
        
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.detail || 'Failed to upload JD');
        }
        
        return response.json();
    },
    
    listJDs: () => apiFetch('/client/jd/list'),
    
    deleteJD: (jdId) => apiFetch(`/client/jd/${jdId}`, { method: 'DELETE' }),
    
    matchCandidates: (jdId, jdText, jobTitle) => apiFetch('/client/match-candidates', {
        method: 'POST',
        body: JSON.stringify({ jd_id: jdId, jd_text: jdText, job_title: jobTitle }),
    }),
    
    messageCandidate: (userId, content, messageType = 'text') => apiFetch('/client/message-candidate', {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, content, message_type: messageType }),
    }),
    
    getCvDetails: (cvId) => apiFetch(`/client/cv/${cvId}`),
};
